"use client";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FileX, X } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/app/_module/components/ui/radio-group";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Label } from "@/app/_module/components/ui/label";

const NO_SALE_REASONS = [
  "Already taken care of",
  "No longer interested",
  "No urgency",
  "Price/budget concerns",
  "Went with another carrier",
] as const;

interface NoSaleReasonModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
  // Optional overrides so the same popup can be tailored per surface. The phone
  // dialer/pipeline keep the defaults above; the door-knock routes screen passes
  // its own field-specific reasons + copy. Falls back to defaults when omitted.
  reasons?: readonly string[];
  title?: string;
  description?: string;
}

const NoSaleReasonModal = function ({
  isOpen,
  onOpenChange,
  onConfirm,
  reasons,
  title = "No Sale Reason",
  description = "Please select the reason this lead did not result in a sale.",
}: NoSaleReasonModalProps) {
  const reasonList = reasons && reasons.length > 0 ? reasons : NO_SALE_REASONS;
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isOther = selectedReason === "other";
  const finalReason = isOther ? otherText.trim() : selectedReason;
  const canSubmit = finalReason.length > 0 && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm(finalReason);
      setSelectedReason("");
      setOtherText("");
      onOpenChange(false);
    } catch {
      // Keep modal open for retry
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedReason("");
    setOtherText("");
    onOpenChange(false);
  };

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 999999 }}>
      {/* Overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          zIndex: 999999,
        }}
        onClick={handleCancel}
      />
      {/* Content */}
      <div
        style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000000,
          width: "95vw",
          maxWidth: "640px",
        }}
        className="bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto"
      >
        {/* Close button */}
        <button
          onClick={handleCancel}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
              <FileX className="w-4 h-4 text-gray-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
          </div>
        </div>

        <p className="px-6 pb-2 text-sm text-gray-600">
          {description}
        </p>

        <div className="px-6 pb-4">
          <RadioGroup
            value={selectedReason}
            onValueChange={(value) => {
              setSelectedReason(value);
              if (value !== "other") setOtherText("");
            }}
            className="space-y-2"
          >
            {reasonList.map((reason) => (
              <div
                key={reason}
                className="flex items-center space-x-3 rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setSelectedReason(reason)}
              >
                <RadioGroupItem value={reason} id={`reason-${reason}`} />
                <Label
                  htmlFor={`reason-${reason}`}
                  className="text-sm text-gray-700 cursor-pointer flex-1"
                >
                  {reason}
                </Label>
              </div>
            ))}

            <div
              className="rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSelectedReason("other")}
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="other" id="reason-other" />
                <Label
                  htmlFor="reason-other"
                  className="text-sm text-gray-700 cursor-pointer flex-1"
                >
                  Other
                </Label>
              </div>
              {isOther && (
                <div className="mt-3 ml-7">
                  <Textarea
                    placeholder="Please describe the reason..."
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    className="resize-none text-sm"
                    rows={2}
                    maxLength={500}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </RadioGroup>
        </div>

        <div className="px-6 pb-6 pt-0 flex flex-col sm:flex-row gap-3 sm:justify-end">
          <Button
            className="px-4 py-2 w-full sm:w-auto"
            onClick={handleCancel}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="px-4 py-2 w-full sm:w-auto"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? "Updating..." : "Confirm No Sale"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NoSaleReasonModal;
